/**
 * 운반 도움 분류 (운반도움 필수 / 할인 / 불필요).
 *
 * items.csv 의 물품 이름 및 옵션 이름(plus_name/extra_name) 뒤 태그로 분류한다:
 * - `#help` → 'required' (운반도움 필수)
 * - `#h3`   → 'discount' (선택 시 출발지/도착지 각 3% 할인)
 * - `#hno`  → 'none'     (운반도움 불필요)
 * - 태그 없음 → 'none'
 *
 * 고객이 선택한 옵션 태그가 아이템 태그를 덮어쓸 수 있으며, 충돌 시 우선순위는
 * 필수 > 할인 > 불필요 (아이템 + 선택된 옵션들 중 가장 강한 분류 적용).
 */

export type CarryHelpClass = 'required' | 'discount' | 'none';

/** 운반도움 할인율 (출발지/도착지 각 side 당) */
export const CARRY_HELP_DISCOUNT_RATE = 0.03;

const RANK: Record<CarryHelpClass, number> = { none: 0, discount: 1, required: 2 };

/** 두 분류 중 더 강한(우선순위 높은) 분류를 반환. 필수 > 할인 > 불필요 */
export function maxCarryHelp(a: CarryHelpClass, b: CarryHelpClass): CarryHelpClass {
  return RANK[a] >= RANK[b] ? a : b;
}

/** 원본 문자열(물품/옵션 이름)의 태그를 분류로 변환. 태그가 없으면 null. */
export function parseCarryHelpTag(raw: string): CarryHelpClass | null {
  if (/#help\b/i.test(raw)) return 'required';
  if (/#h3\b/i.test(raw)) return 'discount';
  if (/#hno\b/i.test(raw)) return 'none';
  return null;
}

/** 화면 표시용 이름에서 운반도움 태그 토큰을 제거. */
export function stripCarryHelpTags(raw: string): string {
  return raw.replace(/#(help|h3|hno)\b/gi, '').trim();
}

interface ResolveOptionGroup {
  type?: string;
  names: string[];
  carryHelp?: CarryHelpClass[];
}

/**
 * 한 물품의 유효 분류를 계산한다.
 * 아이템 태그 + 현재 선택된 plus(select/checkbox)·extra(checkbox) 옵션의 태그를
 * max-precedence(필수 > 할인 > 불필요)로 합산.
 */
export function resolveItemCarryHelp(
  item: {
    carryHelp?: CarryHelpClass | null;
    plus?: ResolveOptionGroup | null;
    extra?: ResolveOptionGroup | null;
  },
  options: { plusValue?: unknown; extraValue?: unknown } = {},
): CarryHelpClass {
  let result: CarryHelpClass = item.carryHelp ?? 'none';

  // plus(select): plusValue 문자열과 일치하는 옵션
  // plus(checkbox): plusValue 배열의 각 항목
  if (item.plus?.carryHelp) {
    const pv = options.plusValue;
    if (item.plus.type === 'select' && typeof pv === 'string') {
      const idx = item.plus.names.indexOf(pv);
      if (idx >= 0 && item.plus.carryHelp[idx]) result = maxCarryHelp(result, item.plus.carryHelp[idx]);
    } else if (item.plus.type === 'checkbox' && Array.isArray(pv)) {
      for (const v of pv as string[]) {
        const idx = item.plus.names.indexOf(v);
        if (idx >= 0 && item.plus.carryHelp[idx]) result = maxCarryHelp(result, item.plus.carryHelp[idx]);
      }
    }
  }

  // extra(checkbox): extraValue 배열의 각 항목
  if (item.extra?.carryHelp) {
    const ev = options.extraValue;
    if (Array.isArray(ev)) {
      for (const v of ev as string[]) {
        const idx = item.extra.names.indexOf(v);
        if (idx >= 0 && item.extra.carryHelp[idx]) result = maxCarryHelp(result, item.extra.carryHelp[idx]);
      }
    }
  }

  return result;
}

/**
 * 주문 전체(확정 items)의 운반도움 모드 + 할인 base 계산.
 * - mode: 모든 물품 분류 중 가장 강한 분류 (필수 > 할인 > 불필요)
 * - discountBase: 'discount' 분류 물품들의 basePrice 합 (할인은 해당 물품에만 적용)
 *
 * 각 item 은 분류(carryHelp)가 이미 스냅샷되어 있어야 한다 (Step1ItemInfo 또는 서버 재해석).
 */
export function resolveOrderCarryHelp(
  items: Array<{ carryHelp?: CarryHelpClass | null; basePrice?: number }>,
): { mode: CarryHelpClass; discountBase: number } {
  let mode: CarryHelpClass = 'none';
  let discountBase = 0;
  for (const it of items) {
    const c = it.carryHelp ?? 'none';
    mode = maxCarryHelp(mode, c);
    if (c === 'discount') discountBase += it.basePrice || 0;
  }
  return { mode, discountBase };
}
